// Final Analytical Review Card V2 — the sections of the 316(a) tab: FS lines (design 1), planning vs completion (2), ratios (3),
// expectations (4), balance-sheet relationships (6) and the conclusion (8). The container (FinalAnalyticalReview.jsx) loads the
// payload and passes the actions; these parts only render.
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { asArray } from "@/lib/asArray";
import { fmt, fmtPct, fill, L, RemarkCell, LinkChips, WorkDoneCell, useColumnWidths, ResizeHandle } from "@/components/far/FarParts";
import ExpectationOverride from "@/components/far/FarExpectationOverride";
import { highlightWhy } from "@/components/far/farHighlightWhy";   // Batch 34 · B34-2
import { AprBeneath } from "@/components/restatement/RestatementViews";   // Prior-Period Restatement · Phase 3b
import { useResizableColumns, ResizableTable, ResizableTh as ColTh, ResetColumnWidths } from "@/components/ui/resizable-table";   // Step 11d · add-on

const th = "px-2 py-1.5 text-start text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";
const td = "px-2 py-1.5 align-top text-xs";
const num = `${td} text-end tabular-nums`;

function Section({ title, testid, children }) {
  return (
    <section className="rounded-md border" data-testid={testid}>
      <h3 className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold dark:bg-slate-900/50">{title}</h3>
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}

// Step 11d · add-on: ``from`` = the column index of the first layer head in an adjustable table (each head gets a handle)
function LayerHeads({ t, layers, lang, from }) {
  const txt = (k, fb) => (lang === "ar" ? layers?.[k]?.ar : layers?.[k]?.en) || t(fb);
  const H = from === undefined ? "th" : ColTh;
  const ix = (i) => (from === undefined ? {} : { index: from + i });
  return (
    <>
      <H {...ix(0)} className={`${th} text-end`} data-testid="far-head-cy-adj">{txt("cy_adj", "far.layer.cyAdj")}</H>
      <H {...ix(1)} className={`${th} text-end`} data-testid="far-head-cy-unadj">{txt("cy_unadj", "far.layer.cyUnadj")}</H>
      <H {...ix(2)} className={`${th} text-end`} data-testid="far-head-py">{txt("py", "far.layer.py")}</H>
    </>
  );
}

// Batch 33 · B33-3: default widths (px) of the FS-lines columns; the user's own widths (dragged) are kept per user in the browser
const LINE_COLS = { line: 240, cy_adj: 130, cy_unadj: 130, py: 120, movement: 120, movement_pct: 90, aje_effect: 110,
                    remark: 260, work_done: 280 };

// Step 11d · add-on: the other FAR tables use the shared primitive (components/ui/resizable-table.jsx), widths per browser
// ratio, CY adj., CY unadj., PY (layer heads), change, planning 205(a), remark
const RATIO_COLS_DEF = [240, 130, 130, 120, 110, 130, 260];
const RATIO_COLS_MIN = [160, 96, 96, 96, 90, 96, 160];
// expectation, formula, expected, actual, difference, remark, (actions)
const EXP_COLS_DEF = [220, 260, 120, 120, 120, 240, 120];
const EXP_COLS_MIN = [150, 140, 96, 96, 96, 140, 80];
// relationship, ratio value, data value, difference
const REL_COLS_DEF = [300, 120, 280, 120];
const REL_COLS_MIN = [160, 96, 140, 96];

function ResizableTh({ col, widths, onStart, className = th, children, testid }) {
  return (
    <th className={`${className} relative`} style={{ width: `${widths[col]}px` }} data-testid={testid || `far-col-${col}`}>
      {children}
      <ResizeHandle col={col} onStart={onStart} />
    </th>
  );
}

export function LinesSection({ data, t, lang, locked, actions, busyKey, colKey = "anon" }) {
  const lines = asArray(data?.lines);
  const [widths, startDrag, resetLineWidths] = useColumnWidths(`azex.far.colWidths.${colKey}`, LINE_COLS, lang === "ar");   // PR 51 · P51-S4
  // Step 11d · add-on 4: the FS lines keep their own per-user widths (PR 51); the shared "Reset column widths" control clears them
  const lineCols = { storageKey: "far-lines", resetWidths: resetLineWidths,
                     isDefault: Object.keys(LINE_COLS).every((k) => widths[k] === LINE_COLS[k]) };
  const total = Object.keys(LINE_COLS).reduce((s, k) => s + (widths[k] || LINE_COLS[k]), 0);
  const layerTxt = (k, fb) => (lang === "ar" ? data?.layers?.[k]?.ar : data?.layers?.[k]?.en) || t(fb);
  const H = (col, label, end = false) => (
    <ResizableTh col={col} widths={widths} onStart={startDrag} className={end ? `${th} text-end` : th}>{label}</ResizableTh>
  );
  return (
    <Section title={t("far.sec.lines")} testid="far-lines">
      <ResetColumnWidths cols={lineCols} className="px-2 pt-1" />
      <table className="border-collapse" style={{ tableLayout: "fixed", width: `${total}px`, minWidth: "100%" }}>
        <thead className="border-b">
          <tr>
            {H("line", t("far.col.line"))}
            <ResizableTh col="cy_adj" widths={widths} onStart={startDrag} className={`${th} text-end`} testid="far-head-cy-adj">
              {layerTxt("cy_adj", "far.layer.cyAdj")}</ResizableTh>
            <ResizableTh col="cy_unadj" widths={widths} onStart={startDrag} className={`${th} text-end`} testid="far-head-cy-unadj">
              {layerTxt("cy_unadj", "far.layer.cyUnadj")}</ResizableTh>
            <ResizableTh col="py" widths={widths} onStart={startDrag} className={`${th} text-end`} testid="far-head-py">
              {layerTxt("py", "far.layer.py")}</ResizableTh>
            {H("movement", t("far.col.movement"), true)}
            {H("movement_pct", t("far.col.movementPct"), true)}
            {H("aje_effect", t("far.col.ajeEffect"), true)}
            {H("remark", t("far.col.remark"))}
            {H("work_done", t("far.col.links"))}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            // Batch 33 · B33-3 (owner): a highlighted line keeps a light tint only — no "Large movement" tag text
            <tr key={l.key} className={`border-b ${l.highlight ? "bg-amber-50/70 dark:bg-amber-950/20" : ""} ${l.kind === "subtotal" ? "font-semibold" : ""}`}
                data-highlight={l.highlight ? "1" : "0"} data-testid={`far-line-${l.key}`}
                data-highlight-reason={l.highlight_reason || ""} title={highlightWhy(l, data?.thresholds, t) || undefined}>
              <td className={`${td} break-words`}>
                {l.code && l.kind !== "subtotal" && <span className="me-1 font-mono text-[10px] text-muted-foreground">{l.code}</span>}
                {L(l, "name", lang)}
              </td>
              <td className={num}>{fmt(l.cy_adj)}</td>
              <td className={num}>{fmt(l.cy_unadj)}</td>
              <td className={num}>{fmt(l.py)}<AprBeneath row={l} fmt={fmt} /></td>
              <td className={num}>{fmt(l.movement)}</td>
              <td className={num}>{fmtPct(l.movement_pct)}</td>
              <td className={num}>{Number(l.aje_effect || 0) ? fmt(l.aje_effect) : "—"}</td>
              <td className={td}>
                <RemarkCell itemKey={l.key} remark={l.remark} t={t} locked={locked} busy={busyKey === l.key}
                            onChange={actions.setRemark} onConfirm={actions.confirmRemark} onDraft={actions.draft} />
              </td>
              <td className={td}>
                {l.kind === "subtotal"
                  ? <LinkChips links={l.links} t={t} testid={`far-links-${l.key}`} />
                  : <WorkDoneCell itemKey={l.key} links={l.links} workDone={l.work_done} t={t} locked={locked}
                                  onChange={actions.setWorkDone} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

export function RatiosSection({ data, t, lang, locked, actions, busyKey }) {
  const cols = useResizableColumns({ storageKey: "far-ratios", defaults: RATIO_COLS_DEF, mins: RATIO_COLS_MIN });   // Step 11d · add-on
  const ratios = asArray(data?.ratios);
  return (
    <Section title={t("far.sec.ratios")} testid="far-ratios">
      <ResizableTable cols={cols} className="w-full border-collapse" data-testid="far-ratios-table">
        <thead className="border-b">
          <tr>
            <ColTh index={0} className={th}>{t("far.col.ratio")}</ColTh>
            <LayerHeads t={t} layers={data?.layers} lang={lang} from={1} />
            <ColTh index={4} className={`${th} text-end`}>{t("far.col.change")}</ColTh>
            <ColTh index={5} className={`${th} text-end`}>{t("far.col.planning205a")}</ColTh>
            <ColTh index={6} className={th}>{t("far.col.remark")}</ColTh>
          </tr>
        </thead>
        <tbody>
          {ratios.map((r) => (
            <tr key={r.key} className="border-b" data-testid={`far-ratio-${r.key}`}>
              <td className={td}>
                {L(r, "name", lang)}
                {r.crossed && (
                  <span className="ms-2 inline-block rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-900 dark:bg-sky-950 dark:text-sky-100"
                        data-testid={`far-crossed-${r.key}`}>{t("far.crossed")}</span>
                )}
              </td>
              <td className={num}>{fmt(r.cy_adj)}</td>
              <td className={num}>{fmt(r.cy_unadj)}</td>
              <td className={num}>{fmt(r.py)}<AprBeneath row={r} fmt={fmt} /></td>
              <td className={num}>{fmt(r.change)}</td>
              <td className={num}>{r.planning_205a === null || r.planning_205a === undefined || r.planning_205a === "" ? "—"
                : (typeof r.planning_205a === "number" ? fmt(r.planning_205a) : String(r.planning_205a))}</td>
              <td className={td}>
                <RemarkCell itemKey={r.key} remark={r.remark} t={t} locked={locked} busy={busyKey === r.key}
                            onChange={actions.setRemark} onConfirm={actions.confirmRemark} />
              </td>
            </tr>
          ))}
        </tbody>
      </ResizableTable>
    </Section>
  );
}

export function PlanningSection({ data, t, lang }) {
  const p = data?.planning || {};
  const byKey = Object.fromEntries(asArray(data?.lines).map((l) => [l.key, l]));
  const rByKey = Object.fromEntries(asArray(data?.ratios).map((r) => [r.key, r]));
  const aje = asArray(p.aje_lines).map((k) => byKey[k]).filter(Boolean);
  const crossed = asArray(p.ratios_crossed).map((k) => rByKey[k]).filter(Boolean);
  const rows205 = asArray(p.form_205a?.rows);
  return (
    <Section title={t("far.sec.planning")} testid="far-planning">
      <div className="grid gap-4 p-3 md:grid-cols-3">
        <div data-testid="far-planning-aje">
          <div className="mb-1 text-xs font-semibold">{t("far.planningAje")}</div>
          {aje.length === 0 ? <div className="text-xs text-muted-foreground">{t("far.planningNoAje")}</div> : (
            <ul className="space-y-0.5 text-xs">
              {aje.map((l) => (
                <li key={l.key} className="flex justify-between gap-2" data-testid={`far-planning-aje-${l.key}`}>
                  <span>{L(l, "name", lang)}</span><span className="tabular-nums">{fmt(l.aje_effect)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div data-testid="far-planning-ratios">
          <div className="mb-1 text-xs font-semibold">{t("far.planningRatios")}</div>
          {crossed.length === 0 ? <div className="text-xs text-muted-foreground">{t("far.planningNoRatios")}</div> : (
            <ul className="space-y-0.5 text-xs">
              {crossed.map((r) => (
                <li key={r.key} className="flex justify-between gap-2">
                  <span>{L(r, "name", lang)}</span>
                  <span className="tabular-nums">{fmt(r.cy_unadj)} → {fmt(r.cy_adj)}</span>
                </li>
              ))}
            </ul>
          )}
          {(p.crossed_rule_en || p.crossed_rule_ar) && (
            <div className="mt-1 text-[11px] text-muted-foreground">{lang === "ar" ? (p.crossed_rule_ar || p.crossed_rule_en) : p.crossed_rule_en}</div>
          )}
        </div>
        <div data-testid="far-planning-205a">
          <div className="mb-1 text-xs font-semibold">{t("far.planning205a")}</div>
          {rows205.length === 0 ? <div className="text-xs text-muted-foreground">{t("far.planning205aEmpty")}</div> : (
            <table className="w-full text-[11px]">
              <tbody>
                {rows205.map((row, i) => (
                  <tr key={i} className="border-b">
                    {Object.entries(row || {}).map(([k, v]) => <td key={k} className="px-1 py-0.5">{typeof v === "number" ? fmt(v) : String(v ?? "")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Section>
  );
}

function AddExpectation({ t, lang, lines, onAdd, locked }) {
  const [label, setLabel] = useState("");
  const [expected, setExpected] = useState("");
  const [lineKey, setLineKey] = useState("");
  const [formula, setFormula] = useState("");
  const ok = label.trim() && expected !== "" && !Number.isNaN(Number(expected)) && lineKey;
  const submit = async () => {
    const done = await onAdd({ label: label.trim(), expected: Number(expected), line_key: lineKey, formula: formula.trim() });
    if (done) { setLabel(""); setExpected(""); setLineKey(""); setFormula(""); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2 border-t p-3 text-xs" data-testid="far-exp-add">
      <label className="flex flex-col gap-0.5">{t("far.expLabel")}
        <input className="rounded border px-2 py-1 dark:bg-slate-900" value={label} disabled={locked}
               onChange={(e) => setLabel(e.target.value)} data-testid="far-exp-add-label" /></label>
      <label className="flex flex-col gap-0.5">{t("far.expExpected")}
        <input className="w-32 rounded border px-2 py-1 text-end dark:bg-slate-900" inputMode="decimal" value={expected} disabled={locked}
               onChange={(e) => setExpected(e.target.value)} data-testid="far-exp-add-expected" /></label>
      <label className="flex flex-col gap-0.5">{t("far.expLine")}
        <select className="rounded border px-2 py-1 dark:bg-slate-900" value={lineKey} disabled={locked}
                onChange={(e) => setLineKey(e.target.value)} data-testid="far-exp-add-line">
          <option value="">—</option>
          {lines.map((l) => <option key={l.key} value={l.key}>{L(l, "name", lang)}</option>)}
        </select></label>
      <label className="flex min-w-[200px] flex-1 flex-col gap-0.5">{t("far.expFormula")}
        <input className="rounded border px-2 py-1 dark:bg-slate-900" value={formula} disabled={locked}
               onChange={(e) => setFormula(e.target.value)} data-testid="far-exp-add-formula" /></label>
      <Button size="sm" className="h-7" disabled={locked || !ok} onClick={submit} data-testid="far-exp-add-submit">{t("far.add")}</Button>
    </div>
  );
}

export function ExpectationsSection({ data, t, lang, locked, actions, busyKey }) {
  const cols = useResizableColumns({ storageKey: "far-expectations", defaults: EXP_COLS_DEF, mins: EXP_COLS_MIN });   // Step 11d · add-on
  const rows = asArray(data?.expectations);
  return (
    <Section title={t("far.sec.expectations")} testid="far-expectations">
      <ResizableTable cols={cols} className="w-full border-collapse" data-testid="far-expectations-table">
        <thead className="border-b">
          <tr>
            <ColTh index={0} className={th}>{t("far.col.expectation")}</ColTh>
            <ColTh index={1} className={th}>{t("far.col.formula")}</ColTh>
            <ColTh index={2} className={`${th} text-end`}>{t("far.col.expected")}</ColTh>
            <ColTh index={3} className={`${th} text-end`}>{t("far.col.actual")}</ColTh>
            <ColTh index={4} className={`${th} text-end`}>{t("far.col.difference")}</ColTh>
            <ColTh index={5} className={th}>{t("far.col.remark")}</ColTh>
            <ColTh index={6} className={th}></ColTh>
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.key} className="border-b" data-testid={`far-exp-${x.key}`}>
              <td className={td}>
                {L(x, "label", lang)}
                {x.custom && <span className="ms-1 text-[10px] text-muted-foreground">({t("far.custom")})</span>}
                {(x.basis_en || x.basis_ar) && (
                  <div className="text-[10px] text-muted-foreground" data-testid={`far-exp-basis-${x.key}`}>
                    {lang === "ar" ? (x.basis_ar || x.basis_en) : x.basis_en}
                  </div>
                )}
              </td>
              <td className={`${td} max-w-[320px] text-muted-foreground`}>{lang === "ar" ? (x.formula_ar || x.formula_en) : x.formula_en}</td>
              {x.available ? (
                <>
                  <td className={num}>{fmt(x.expected)}</td>
                  <td className={num}>{fmt(x.actual)}</td>
                  <td className={num}>{fmt(x.difference)}</td>
                </>
              ) : (
                <td className={`${td} text-muted-foreground`} colSpan={3} data-testid={`far-exp-nodata-${x.key}`}>
                  {t("far.noData")}{(x.reason_en || x.reason_ar) ? ` — ${lang === "ar" ? (x.reason_ar || x.reason_en) : x.reason_en}` : ""}
                </td>
              )}
              <td className={td}>
                {x.available && (
                  <RemarkCell itemKey={x.key} remark={x.remark} t={t} locked={locked} busy={busyKey === x.key}
                              onChange={actions.setRemark} onConfirm={actions.confirmRemark} />
                )}
              </td>
              <td className={td}>
                {x.custom && (
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" disabled={locked}
                          onClick={() => actions.removeExpectation(x)} data-testid={`far-exp-remove-${x.key}`}>{t("far.remove")}</Button>
                )}
                {!x.custom && (x.available || x.override) && actions.overrideExpectation && (
                  <ExpectationOverride row={x} t={t} locked={locked} onSave={actions.overrideExpectation} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </ResizableTable>
      <div className="px-3 pt-2 text-xs font-semibold">{t("far.addExpectation")}</div>
      <AddExpectation t={t} lang={lang} lines={asArray(data?.lines)} onAdd={actions.addExpectation} locked={locked} />
    </Section>
  );
}

export function RelationshipsSection({ data, t, lang }) {
  const cols = useResizableColumns({ storageKey: "far-relationships", defaults: REL_COLS_DEF, mins: REL_COLS_MIN });   // Step 11d · add-on
  const rows = asArray(data?.relationships);
  return (
    <Section title={t("far.sec.relationships")} testid="far-relationships">
      <ResizableTable cols={cols} className="w-full border-collapse" data-testid="far-relationships-table">
        <thead className="border-b">
          <tr>
            <ColTh index={0} className={th}>{t("far.col.relationship")}</ColTh>
            <ColTh index={1} className={`${th} text-end`}>{t("far.col.ratioValue")}</ColTh>
            <ColTh index={2} className={th}>{t("far.col.dataValue")}</ColTh>
            <ColTh index={3} className={`${th} text-end`}>{t("far.col.difference")}</ColTh>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b" data-testid={`far-rel-${r.key}`}>
              <td className={td}>{L(r, "label", lang)}</td>
              <td className={num}>{fmt(r.ratio_value)}</td>
              {r.available ? (
                <td className={td}>{fmt(r.data_value)} <span className="text-muted-foreground">{L(r, "data_label", lang)}</span></td>
              ) : (
                <td className={`${td} text-muted-foreground`}>{lang === "ar" ? (r.reason_ar || r.reason_en) : r.reason_en}</td>
              )}
              <td className={num}>{r.available ? fmt(r.difference) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </ResizableTable>
    </Section>
  );
}

// Batch 33 · FAR-F5: the evaluation answers are part of the screen's one Save — ``data.conclusion.items`` already carries the
// unsaved answers on top of the saved ones; an unanswered comment starts from the prefill (saved only with an answer + Save).
export function ConclusionSection({ data, t, lang, locked, onChange }) {
  const c = data?.conclusion || {};
  const prefill = (lang === "ar" ? c.prefill_ar : c.prefill_en)
    || fill(t("far.prefill"), { n: c.highlighted ?? 0, m: c.with_remarks ?? 0 });
  const shown = (it) => ({ response: it.response || "", comment: it.comment === undefined || it.comment === "" ? prefill : it.comment });
  const set = (it, k, v) => onChange(it.id, { ...shown(it), [k]: v });
  return (
    <Section title={t("far.sec.conclusion")} testid="far-conclusion">
      <div className="space-y-3 p-3 text-xs">
        <div className="font-medium" data-testid="far-conclusion-prefill">{prefill}</div>
        {asArray(c.items).map((it) => (
          <div key={it.id} className="space-y-1 rounded border p-2" data-testid={`far-eval-${it.id}`}>
            <div>{(lang === "ar" && it.text_ar) || it.text} {it.ref && <span className="text-muted-foreground">({it.ref})</span>}</div>
            <div className="flex flex-wrap gap-3">
              {["Yes", "No", "N/A"].map((r) => (
                <label key={r} className="flex items-center gap-1">
                  <input type="radio" name={`far-eval-${it.id}`} value={r} disabled={locked}
                         checked={shown(it).response === r} onChange={() => set(it, "response", r)}
                         data-testid={`far-eval-${it.id}-${r}`} />
                  {t(`far.response.${r}`)}
                </label>
              ))}
            </div>
            <textarea className="w-full rounded border px-2 py-1 dark:bg-slate-900" rows={2} placeholder={t("far.comment")}
                      value={shown(it).comment} disabled={locked}
                      onChange={(e) => set(it, "comment", e.target.value)} data-testid={`far-eval-${it.id}-comment`} />
          </div>
        ))}
      </div>
    </Section>
  );
}
